import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FormulaParameter, ParameterType, ParameterTypes } from '../../../types/formula.interface';
import { Measure } from '../../../types/measure.interface';
import { Unit } from '../../../types/unit.interface';
import { ParameterMode, PARAMETER_TYPE } from '../inputs/parameters-input/parameter-types';
import { ParameterDisplayRowComponent } from './parameter-display-row.component';

@Component({
  selector: 'ces-parameters-display',
  standalone: true,
  imports: [ParameterDisplayRowComponent, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './parameters-display.component.html',
  styles: `
    ::ng-deep {
      table {
        tr {
          &:not(:last-child) td {
            border-bottom: 1px solid var(--p-gray-200);
          }
        }
      }
    }
  `,
})
export class ParametersDisplayComponent implements OnInit {
  private translateService = inject(TranslocoService);

  @Input() showEmptyMessage = true;
  @Input() emptyMessage?: string;
  @Input() mode: ParameterMode = ParameterMode.FORMULA_MODEL;

  private readonly _parameters = signal<FormulaParameter[]>([]);
  private readonly _units = signal<Unit[]>([]);
  private readonly _measures = signal<Measure[]>([]);

  parameterTypes: { label: string; value: ParameterType }[] = [];

  ngOnInit(): void {
    this.initParameterTypes();
  }

  private initParameterTypes(): void {
    this.parameterTypes = [
      { label: this.translateService.translate('forms.parameterTypes.constant'), value: PARAMETER_TYPE.CONSTANT },
      { label: this.translateService.translate('forms.parameterTypes.input'), value: PARAMETER_TYPE.INPUT },
      { label: this.translateService.translate('forms.parameterTypes.measure'), value: PARAMETER_TYPE.MEASURE },
      { label: this.translateService.translate('forms.parameterTypes.acquisition'), value: PARAMETER_TYPE.ACQUISITION },
    ];
  }

  @Input()
  set parametersInput(value: FormulaParameter[] | null | undefined) {
    if (value) {
      this._parameters.set(value);
    } else {
      this._parameters.set([]);
    }
  }

  @Input()
  set unitsInput(value: Unit[] | null | undefined) {
    if (value) {
      this._units.set(value);
    } else {
      this._units.set([]);
    }
  }

  @Input()
  set measuresInput(value: Measure[] | null | undefined) {
    if (value) {
      this._measures.set(value);
    } else {
      this._measures.set([]);
    }
  }

  readonly parameters = computed(() => this._parameters());
  readonly units = computed(() => this._units());
  readonly measures = computed(() => this._measures());

  hasParamsWithUnitOrMeasure(): boolean {
    return this.hasParamsWithUnit() || this.hasParamsWithMeasure();
  }

  hasParamsWithUnit(): boolean {
    return this.parameters().some(p => p.type === ParameterTypes.CONSTANT || p.type === ParameterTypes.INPUT);
  }

  hasParamsWithMeasure(): boolean {
    return (
      (this.mode === ParameterMode.FORMULA || this.mode === ParameterMode.POLYNOMIAL) &&
      this.parameters().some(p => p.type === ParameterTypes.MEASURE)
    );
  }

  hasParamsWithValue(): boolean {
    return this.parameters().some(p => p.type === ParameterTypes.CONSTANT || p.type === ParameterTypes.INPUT);
  }
}
